import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ButtonInputClear } from 'components/buttons';
import { Close, Lock, Visibility } from 'components/SvgIcons';
import { CFM_STONE, CFM_METAL } from 'constants/Colors';
import './styles.scss';

/**
 * Input Text Component
 */
class InputPassword extends React.PureComponent {
    constructor(props) {
        super(props);

        this.textInput = React.createRef();

        const { value = '' } = this.props;
        this.state = {
            focused: false,
            text: value || '',
            showText: false,
        };
    }

    static getDerivedStateFromProps(props, state) {
        const { value } = props;
        const { text } = state;
        if (value !== text) {
            return {
                text: value,
            };
        }
        return null;
    }

    onFocus = (event) => {
        const { onFocus = () => null } = this.props;
        this.setState({ focused: true });
        onFocus(event);
    };

    onBlur = (event) => {
        const { onBlur = () => null } = this.props;
        this.setState({ focused: false });
        onBlur(event);
    };

    onChange = (e) => {
        const { onChange = () => null } = this.props;
        this.setState({ text: e.target.value });
        if (onChange) {
            onChange(e.target.value);
        }
    };

    onClearHandler = () => {
        this.clearInput();
        this.focusTextInput();
    };

    focusTextInput = () => {
        // Explicitly focus the text input using the raw DOM API
        // Note: we're accessing "current" to get the DOM node
        this.textInput.current.focus();
    };

    clearInput = () => {
        const { onClearClick = () => null } = this.props;
        this.setState({ text: '' }, () => {
            onClearClick();
        });
    };

    toggleShowText = () => {
        this.setState((prevState) => ({
            showText: !prevState.showText,
        }));
    };

    render() {
        const {
            className = '',
            placeholder = '',
            id = '',
            showIcon = true,
            readOnly,
            hasError,
            ...rest
        } = this.props;
        const { showText, text, focused } = this.state;

        const parentClassNames = ['input-password-wrapper'];
        if (focused) {
            parentClassNames.push('password-input--focus');
        }
        if (readOnly) {
            parentClassNames.push('input-password-readonly');
        }
        if (hasError) {
            parentClassNames.push('input-error');
        }
        if (showIcon) {
            parentClassNames.push('password-input__with-icon');
        }

        let classNames = ['password-input'];
        if (className) {
            classNames = classNames.concat(className);
        }

        return (
            <div className={parentClassNames.join(' ')}>
                <input
                    {...rest}
                    ref={this.textInput}
                    id={id}
                    type={!showText ? 'password' : 'text'}
                    className={classNames.join(' ')}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    value={text}
                    onChange={this.onChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    autoComplete="new-password"
                />
                <div className="input-password-icon">
                    {readOnly && (
                        <i className="input-password-icon__lock">
                            <Lock color={CFM_METAL} />
                        </i>
                    )}
                    {showIcon && !readOnly && text.length > 0 && (
                        <Fragment>
                            <ButtonInputClear
                                classNames={['input-password-icon__btn-clear']}
                                onClick={this.onClearHandler}
                                tabIndex="-1"
                            >
                                <i className="input-password-icon__clear">
                                    <Close color={CFM_STONE} />
                                </i>
                            </ButtonInputClear>
                            <button
                                className="input-password-icon__eye"
                                type="button"
                                onClick={this.toggleShowText}
                            >
                                <i>
                                    <Visibility visible={showText} color={CFM_METAL} />
                                </i>
                            </button>
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }
}

InputPassword.propTypes = {
    /** Description of prop "value". */
    value: PropTypes.string,
    /** Description of prop "placeholder". */
    placeholder: PropTypes.string,
    /** Disable input */
    disabled: PropTypes.bool,
    /** Class name property */
    className: PropTypes.string,
    /** input id */
    id: PropTypes.string,
    /** onchange */
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    showIcon: PropTypes.bool,
    onClearClick: PropTypes.func,
    hasError: PropTypes.bool,
    readOnly: PropTypes.bool,
};

export default InputPassword;
